import { Record as record, Map, List } from 'immutable';
import _ from 'lodash';

import Fare from './Fare.js';
import { resolve } from '../helpers/LinkMapper';
import { PlusBusOptions, JourneyDirections } from '../constants/AppConstants';

const defaults = {
    id: null,
    outward: null,
    return: null
};

export const options = [
    ['outward', 'destination'],
    ['outward', 'origin'],
    ['return', 'origin'],
    ['return', 'destination']
];

function makePortionFares (portion = {}) {
    if (Object.keys(portion).length === 0) { return null; }

    return new Map({
        origin: resolve(portion.origin).as(Fare),
        destination: resolve(portion.destination).as(Fare)
    });
}

class PlusBus extends record(defaults) {

    static fromJSON (json = null) {

        // NOTE: Reserved naming issues may arise by using "return"
        const outward = resolve(json.outward).with(makePortionFares);
        const inward = resolve(json.return).with(makePortionFares);

        let result = { outward, return: inward };

        return new PlusBus(result);
    }

    getPortion (direction) { return this.get(direction); }

    getFares () {
        return new List([
            this.getIn(['outward', 'origin']),
            this.getIn(['outward', 'destination']),
            this.getIn(['return', 'origin']),
            this.getIn(['return', 'destination'])
        ]).filter((fare) => !!fare);
    }

    hasTerminal (direction, terminal) {
        const portion = this.getPortion(direction);
        return portion && portion.get(terminal);
    }

    setTerminal (direction, terminal, value) {
        let portion = this.getPortion(direction);
        if (!portion) {
            portion = new Map();
        }
        portion = portion.set(terminal, value);
        return this.set(direction, portion);
    }

    count() {
        return _.filter(options, (args) => this.hasTerminal.apply(this, args)).length;
    }

    getTotalPrice() {
        return _.reduce(options, (total, option) => {
            const terminal = this.getTerminal.apply(this, option);
            if (terminal) {
                return total + terminal.getPrice();
            }
            return total;
        }, 0);
    }

    getCheapestTicket() {
        const outwardPrice = this.outward ? this.outward.map(e => _.get(e, 'totalPrice', 0)) : new Map();
        const returnPrice = this.return ? this.return.map(e => _.get(e, 'totalPrice', 0)) : new Map();

        return Math.min(...[outwardPrice.get("destination"),
            outwardPrice.get("origin"),
            returnPrice.get("destination"),
            outwardPrice.get("origin")].filter(Boolean));
    }

    getTerminal (direction, terminal) {
        const portion = this.getPortion(direction);
        return portion && portion.get(terminal);
    }

    getValidDate (direction) {
        const portion = this.getPortion(direction);
        const fare = portion && (portion.get('origin') || portion.get('destination'));

        if (fare) { return fare.getIn(['outwardValidity', 'until'], null); }
        return null;
    }

    isSameDay () {
        const outwardValidity = this.getValidDate('outward');
        const returnValidity = this.getValidDate('return');
        return outwardValidity && outwardValidity.isSame(returnValidity);
    }

    plusBusAvailable() {
        return (
            (this.outward && (this.outward.get('origin') || this.outward.get('destination'))) ||
            (this.return && (this.return.get('origin') || this.return.get('destination')))
        );
    }

    getDirectionKey(direction, isSameDay) {
        const directionKey =
            isSameDay || this.isSameDay() || direction === JourneyDirections.OUTWARD
                ? 'outward'
                : direction === JourneyDirections.INWARD
                ? 'return'
                : null;
        return directionKey;
    }

    getFaresForDirection(direction, isSameDay) {
        return this.get(this.getDirectionKey(direction, isSameDay));
    }

    setFareForDirection(direction, terminal, fare, isSameDay) {
        const directionKey = this.getDirectionKey(direction, isSameDay);
        let current = this.get(directionKey);
        if (!current) {
            current = new Map();
        }
        current = current.set(terminal, fare);
        return this.set(directionKey, current);
    }

    matchWithOrderPlusBus (plusBus) {
        return PlusBusOptions.reduce((acc, { terminal, direction }) => {
            const fare = plusBus.getTerminal(direction, terminal);
            if (!plusBus.hasTerminal(direction, terminal)
                || !fare.tickets.some(ticket => !ticket.isRefunded)) {
                acc = acc.setTerminal(direction, terminal, null);
            }
            return acc;
        }, this);
    }
}

export default PlusBus;
